import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: 768px) {
    padding: 1.75rem;
  }
`;

const BannerGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const BannerCard = styled(Link)`
  display: block;
  border-radius: 1.5rem;
  padding: 2.5rem;
  background: linear-gradient(135deg, ${({ gradient }) => gradient || 'rgba(102, 126, 234, 0.1)'}, ${({ gradientEnd }) => gradientEnd || 'rgba(118, 75, 162, 0.1)'});
  border: 2px solid ${({ theme }) => theme.colors.primary}20;
  text-decoration: none;
  color: inherit;
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: ${({ theme }) => theme.colors.primary}40;

    &::before {
      transform: scaleX(1);
    }
  }
`;

const BannerIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 12px ${({ theme }) => theme.colors.primary}25;
`;

const BannerTitle = styled.h3`
  margin: 0 0 0.75rem;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 1.75rem;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const BannerDescription = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.6;
  font-size: 1rem;
`;

const BannerArrow = styled.span`
  display: inline-block;
  margin-left: 0.5rem;
  transition: transform 0.3s ease;
  
  ${BannerCard}:hover & {
    transform: translateX(4px);
  }
`;

const programs = [
  {
    id: 'elementary',
    title: '초등 프로그램',
    description: '창의적 사고와 논리적 문제 해결 능력을 키우는 초등 코딩 교육',
    icon: '🎨',
    gradient: 'rgba(102, 126, 234, 0.15)',
    gradientEnd: 'rgba(118, 75, 162, 0.15)',
  },
  {
    id: 'middle',
    title: '중등 프로그램',
    description: '실전 프로젝트와 협업을 통한 중등 코딩 실력 향상 프로그램',
    icon: '🚀',
    gradient: 'rgba(118, 75, 162, 0.15)',
    gradientEnd: 'rgba(102, 126, 234, 0.15)',
  },
  {
    id: 'high',
    title: '고등 프로그램',
    description: '대학 진학과 진로 준비를 위한 고등 코딩 심화 프로그램',
    icon: '🎓',
    gradient: 'rgba(246, 173, 85, 0.15)',
    gradientEnd: 'rgba(102, 126, 234, 0.15)',
  },
  {
    id: 'adult',
    title: '성인 프로그램',
    description: '직장인과 성인을 위한 실무 중심 코딩 교육 프로그램',
    icon: '💼',
    gradient: 'rgba(102, 126, 234, 0.15)',
    gradientEnd: 'rgba(246, 173, 85, 0.15)',
  },
];

const ProgramsPage = () => (
  <Wrapper>
    <SectionHeading
      eyebrow="커리큘럼"
      title="프로그램 안내"
      description="학생의 연령과 수준에 맞는 맞춤형 코딩 교육 프로그램을 제공합니다."
    />
    <BannerGrid>
      {programs.map((program) => (
        <BannerCard
          key={program.id}
          to={`/programs/${program.id}`}
          gradient={program.gradient}
          gradientEnd={program.gradientEnd}
        >
          <BannerIcon>{program.icon}</BannerIcon>
          <BannerTitle>
            {program.title}
            <BannerArrow>→</BannerArrow>
          </BannerTitle>
          <BannerDescription>{program.description}</BannerDescription>
        </BannerCard>
      ))}
    </BannerGrid>
  </Wrapper>
);

export default ProgramsPage;

